/**
 * Exclusive part group — renders a segmented toggle between part variants
 * that share an `exclusiveGroup` name.
 *
 * Supports two modes:
 * - **Single-part variants** (no `exclusiveGroupValue`): Each part is one variant,
 *   only the active part's PartSelector is shown (e.g., legacy per-part toggles).
 * - **Multi-part variants** (`exclusiveGroupValue` set): Parts are grouped by value,
 *   and ALL parts matching the active value are shown (e.g., ZISL vs ZSLE in Cook
 *   catalogs where legs, bridges, extensions all switch together).
 */

import { useMemo, useState } from 'react';

import { PartSelector } from './part-selector';

import type {
    FieldSelections,
    ICatalogPart,
    IntroductionSide,
    MeasurementLookup,
    Side,
} from '@/workflows/vascular/common/catalog';
import { SegmentedToggle } from '@/workflows/vascular/common/components/segmented-toggle';

interface ExclusivePartGroupProps {
    /** Parts sharing the same exclusiveGroup, ordered as in the catalog. */
    parts: ICatalogPart[];
    side: Side;
    introductionSide: IntroductionSide;
    measurementLookup: MeasurementLookup;
    onAdd: (partId: string, side: Side, fieldSelections: FieldSelections) => void;
    disabled?: boolean;
}

export function ExclusivePartGroup({
    parts,
    side,
    introductionSide,
    measurementLookup,
    onAdd,
    disabled,
}: ExclusivePartGroupProps) {
    const hasGroupValues = parts.some((p) => p.exclusiveGroupValue);

    if (hasGroupValues) {
        return (
            <MultiPartVariantGroup
                parts={parts}
                side={side}
                introductionSide={introductionSide}
                measurementLookup={measurementLookup}
                onAdd={onAdd}
                disabled={disabled}
            />
        );
    }

    return (
        <SinglePartVariantGroup
            parts={parts}
            side={side}
            introductionSide={introductionSide}
            measurementLookup={measurementLookup}
            onAdd={onAdd}
            disabled={disabled}
        />
    );
}

// ============================================================================
// Single-part variants (original behavior)
// ============================================================================

function SinglePartVariantGroup({
    parts,
    side,
    introductionSide,
    measurementLookup,
    onAdd,
    disabled,
}: ExclusivePartGroupProps) {
    const defaultIndex = parts.findIndex((p) => p.defaultInExclusive);
    const [activeIndex, setActiveIndex] = useState(defaultIndex >= 0 ? defaultIndex : 0);

    const toggleOptions = useMemo(() => parts.map((part, i) => ({ value: i, label: part.label })), [parts]);

    const activePart = parts[activeIndex];
    if (!activePart) return null;

    return (
        <div className="border-b border-border/50">
            {parts.length > 1 && (
                <div className="px-3 pt-2 pb-1">
                    <SegmentedToggle options={toggleOptions} value={activeIndex} onChange={setActiveIndex} />
                </div>
            )}
            <PartSelector
                key={`${activePart.id}-${side}`}
                part={activePart}
                side={side}
                introductionSide={introductionSide}
                measurementLookup={measurementLookup}
                onAdd={onAdd}
                disabled={disabled}
            />
        </div>
    );
}

// ============================================================================
// Multi-part variants (all parts with same exclusiveGroupValue switch together)
// ============================================================================

function MultiPartVariantGroup({
    parts,
    side,
    introductionSide,
    measurementLookup,
    onAdd,
    disabled,
}: ExclusivePartGroupProps) {
    // Collect distinct variant values, preserving order
    const variantValues = useMemo(() => {
        const seen = new Set<string>();
        const values: string[] = [];
        for (const p of parts) {
            const v = p.exclusiveGroupValue ?? p.id;
            if (!seen.has(v)) {
                seen.add(v);
                values.push(v);
            }
        }
        return values;
    }, [parts]);

    // Find default variant
    const defaultVariant = parts.find((p) => p.defaultInExclusive)?.exclusiveGroupValue ?? variantValues[0];
    const [activeVariant, setActiveVariant] = useState(defaultVariant);

    const toggleOptions = useMemo(() => variantValues.map((v) => ({ value: v, label: v })), [variantValues]);

    const activeParts = useMemo(
        () => parts.filter((p) => p.exclusiveGroupValue === activeVariant),
        [parts, activeVariant]
    );

    return (
        <div className="border-b border-border/50">
            {variantValues.length > 1 && (
                <div className="px-3 pt-2 pb-1">
                    <SegmentedToggle
                        options={toggleOptions}
                        value={activeVariant}
                        onChange={setActiveVariant}
                    />
                </div>
            )}
            {activeParts.map((part) => (
                <PartSelector
                    key={`${part.id}-${side}`}
                    part={part}
                    side={side}
                    introductionSide={introductionSide}
                    measurementLookup={measurementLookup}
                    onAdd={onAdd}
                    disabled={disabled}
                />
            ))}
        </div>
    );
}
